// MIDAS V3 — Streaming markdown (progressive prose during a stream)
//
// Mirrors V2's StreamingMarkdownV2 (ChatMessageListV2.tsx:162-219). Same
// shape, same parse-safety pipeline, same handoff behaviour. Where V2 uses
// MUI / Tailwind utility classes, this V3 component uses plain inline
// styles and the V3 design tokens (--ink, --card, --font-sans, --accent).
//
// Why this exists:
//   The V3 chat-message list previously rendered ONLY the streaming chrome
//   (animated hairline + step pill + elapsed timer) until the entire bot
//   response landed in state.messages at sendMessage.fulfilled. The slice's
//   `streamingText` field — populated per token via appendStreamingToken —
//   was never consumed by V3, so users saw only the chrome with no progress
//   text. V1 and V2 both subscribe to `threadBuffer.streamingText` and
//   render it through a streaming markdown sibling; this component is V3's
//   equivalent.
//
// Handoff pattern (mirrors V2 exactly):
//   • isLoading && !streamingText → render StreamingStateV3 chrome (V3 hub
//     handles this branch — this component is NOT rendered).
//   • isLoading && streamingText  → render this component INSTEAD of the
//     chrome. Once the first token arrives the timer is irrelevant; what
//     the user cares about is time-to-first-token, not time-to-completion.
//
// Parse safety:
//   The bot may be partway through emitting a `<list-ref>` tag at the moment
//   we render. We truncate the buffer at the `safe` portion via
//   trimUnclosedListRefTail so a half-open tag never reaches the markdown
//   parser. Once the tag closes (or the stream ends) the cut moves forward
//   and the chip materialises.
//
// Visual parity:
//   We import the same buildMarkdownComponents factory used by
//   ChatBotMessageV3. This guarantees the prose renders with identical p /
//   ul / ol / li / code / table / strong / cite overrides — no visual pop
//   when the stream ends and the final message replaces this surface.
//
// Accessibility:
//   The outer wrapper is aria-live="polite" so screen-reader users hear
//   the growing prose as new tokens arrive (without interrupting other
//   speech). aria-atomic="false" lets readers announce only the appended
//   delta rather than re-read the whole buffer on every chunk.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens, no useRedesignSetup, no
// RedesignXxx components.
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { trimUnclosedRefTagTail } from "../../../../shared/chatBot/streamBotMessage";
import useTranslation from "../../../../shared/hooks/useTranslation";
import MidasLogoV3 from "./MidasLogoV3";
import {
  buildMarkdownComponents,
  rewriteAllRefsToSentinels,
} from "./markdownComponentsV3";
// V3-SEGMENT-STREAMING (SP-1): live block-wise rendering — hold back a partial
// marker line, split the safe buffer into ordered blocks by wire order, render
// prose blocks as markdown and marker lines as gray step rows. Mirrors the
// final-message path in ChatBotMessageV3 so there is no visual pop at handoff.
import {
  holdPartialMarkerTail,
  splitMarkerBlocks,
} from "./markerBlocksV3";
import StepRowV3 from "./StepRowV3";

// Task 2 (TEL_REF_RENDERER_SPEC §6A.2): the chip tags whose half-open tails the
// streaming trimmer holds back AND that rewriteAllRefsToSentinels collapses to
// chips mid-stream. Module-const (stable reference) so the useMemo([text]) dep
// stays minimal. page-ref is included for symmetry — it is part of
// rewriteAllRefsToSentinels (markdownComponentsV3 §5 step 4), so a streamed
// page-ref also renders live and is tail-trimmed.
const CHIP_TAGS = [
  "list-ref",
  "tel-ref",
  "live-ref",
  "plc-ref",
  "page-ref",
  "val",
];

export interface StreamingMarkdownV3Props {
  /** Live streaming buffer (may be partial, may end mid-tag). */
  text: string;
  /** Epoch ms when the stream began — drives the dotted timestamp. */
  startedAt: number;
}

const formatTime = (ts: number): string => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const StreamingMarkdownV3: React.FC<StreamingMarkdownV3Props> = ({
  text,
  startedAt,
}) => {
  const { translate } = useTranslation("chatBot");

  // V3-SEGMENT-STREAMING (SP-1): block-wise streaming pipeline. Order:
  //   1. holdPartialMarkerTail — withhold a half-streamed marker line so
  //      `[Step: Sea` never flashes as prose (marker-line twin of the chip
  //      hold-back below; the held tail reappears as a gray row once its `]`
  //      streams in).
  //   2. splitMarkerBlocks — turn the safe buffer into ordered blocks (text vs
  //      marker) by wire order alone.
  //   3. per TEXT block: chip hold-back + sentinel rewrite (Task 2 /
  //      TEL_REF_RENDERER_SPEC §6A.2) — trimUnclosedRefTagTail holds a half-open
  //      chip tag (opening- OR closing-tag split, §6A.7), then
  //      rewriteAllRefsToSentinels collapses every COMPLETE chip tag to a
  //      sentinel for chip substitution. The indexOf("<")===-1 short-circuit
  //      skips the ~6 regex passes on plain-prose blocks (§6A.4).
  // We deliberately do NOT add preprocessMarkdown here (mid-stream bullet
  // re-stitching would reflow paragraphs on every token); the finalized message
  // applies it once per block. Marker content is NEVER chip/markdown-processed —
  // it renders as plain text via StepRowV3.
  const blocks = useMemo(() => {
    const { safe } = holdPartialMarkerTail(text);
    return splitMarkerBlocks(safe).map((b) => {
      if (b.kind !== "text") return b;
      const chipSafe = trimUnclosedRefTagTail(b.text, CHIP_TAGS).safe;
      const processed =
        chipSafe.indexOf("<") === -1
          ? chipSafe
          : rewriteAllRefsToSentinels(chipSafe);
      return { ...b, processed };
    });
  }, [text]);

  // We don't have an artifact-active page in the streaming surface (the
  // user can't have opened a citation yet — the stream is mid-flight), so
  // resolveActive is a no-op constant. The markdown renderers happily
  // render <CiteV3> chips in their default (inactive) state.
  const resolveActive = useMemo(() => () => false, []);

  // Use the SAME markdown component factory the final message uses. This is
  // the hard requirement of the streaming-parity fix: no visual pop when the
  // stream ends and ChatBotMessageV3 takes over.
  const markdownComponents = useMemo(
    () => buildMarkdownComponents(resolveActive),
    [resolveActive]
  );

  const streamingLabel = translate("midas.v3.streaming.label", {
    defaultValue: "streaming",
  }) as string;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      style={{
        marginBottom: 22,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {/* Header strip — matches the final-message header from
          ChatBotMessageV3 so the transition from streaming → final does not
          shift the surrounding chrome. Only difference: the streaming chip
          is "STREAMING" in accent colour (vs. "REPLIED" in faint) so the
          state is visible while it lasts. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <MidasLogoV3 size={26} streaming halo />
        <span
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: "var(--ink)",
            fontFamily: "var(--font-sans)",
          }}
        >
          Midas
        </span>
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--accent)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          {streamingLabel}
        </span>
        <span
          className="mono"
          aria-hidden="true"
          style={{
            fontSize: 11,
            color: "var(--faint)",
            letterSpacing: "0.04em",
          }}
        >
          · {formatTime(startedAt)}
        </span>
      </div>

      {/* Body — the progressive prose. We render directly (no card chrome).
          SP-1V (2026-07-12): the final message is now ALSO chrome-less
          (ResponseCardV3 `flat` from ChatBotMessageV3), so there is no card to
          settle into — the streamed prose stays visually in place at handoff,
          matching the §1A Claude reference. The `padding: 0 2px` here is kept
          in sync with the flat final body so the column does not shift. */}
      <div
        style={{
          padding: "0 2px",
          fontSize: 14,
          lineHeight: 1.5,
          color: "var(--ink)",
          fontFamily: "var(--font-sans)",
        }}
      >
        {/* SP-1: ordered blocks — marker rows + prose siblings in wire order. */}
        {blocks.map((b, i) =>
          b.kind === "marker" ? (
            <StepRowV3 key={`blk-${i}`} label={b.label} />
          ) : (
            <ReactMarkdown
              key={`blk-${i}`}
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {b.processed}
            </ReactMarkdown>
          )
        )}
      </div>
    </div>
  );
};

export default StreamingMarkdownV3;
